/**
 * THE LEDGER ON SCREEN.
 *
 * Two places, one renderer. On a customer it is their conversation; on its
 * own page it is the whole shop's traffic. Writing those as two components
 * is how the old system ended up with a Comms page and a customer timeline
 * that disagreed about what counted as a message.
 *
 * A call and a text do not look the same on purpose. A call has a length
 * and sometimes a recording; a text has words. Rendering both as a generic
 * "event" throws away the one thing that tells you which you are looking
 * at from across the room.
 */

import { commsForCustomer, recentComms, spoken, type Comm } from "../data/comms.ts";
import { deliveryLabel, neverArrived } from "../domain/delivery.ts";
import { esc, ago, mount } from "../ui/dom.ts";
import { icon } from "../ui/icons.ts";
import { whoAmI, type Who } from "../data/session.ts";
import { db, api } from "../data/client.ts";
import { startLive } from "../data/live.ts";
import { callButtonsHtml, wireCallButtons } from "../ui/callplay.ts";

/**
 * WHO GETS TO TEXT FROM THE PANEL.
 *
 * It started as the main desk only. Anthony: "First only for our side, no
 * other shops need this function yet." It was proven there, and on
 * 2026-09-29 he opened it up for Swift: "add the ability for them to answer
 * their customers in comms like a normal pro account or home shop
 * supercanic account."
 *
 * So the rule is now the one the Send button on a customer has always used
 * (server/routes/sms.ts): a shop with its OWN texting line gets the
 * two-pane composer, and the main desk always does. A shop with no line
 * keeps the read-only ledger below, because a reply from a number that is
 * not theirs would land on somebody else's front desk.
 */
const MAIN_DESK = "7097c673-aa9f-4f0e-ba24-75050a5a29ae";
const canTextFrom = (who: Who): boolean =>
  who.state === "in" && (who.account.id === MAIN_DESK || !!who.account.sms_number);

type Thread = { phone: string; name: string; rows: Comm[]; last: Comm; waiting: boolean };
const ten = (s: string | null | undefined): string => String(s ?? "").replace(/\D/g, "").slice(-10);
const prettyPhone = (p: string): string =>
  p.length === 10 ? `(${p.slice(0, 3)}) ${p.slice(3, 6)}-${p.slice(6)}` : p;

async function bearer(): Promise<string> {
  const sb = await db();
  const { data: { session } } = await sb.auth.getSession();
  return session?.access_token ?? "";
}

/**
 * Can the signed-in shop text from the panel? Cached after the first
 * answer, because a customer's Comms tab and every lead card ask it, and
 * asking whoAmI each time is a round trip for a thing that does not change
 * inside a session. Exported so the lead card can offer the same jump.
 */
let panelCache: boolean | null = null;
export async function canTextFromPanel(): Promise<boolean> {
  if (panelCache !== null) return panelCache;
  try { panelCache = canTextFrom(await whoAmI()); }
  catch { panelCache = false; }
  return panelCache;
}

/**
 * THE WAY INTO THE PANEL, from anywhere a conversation is shown read-only.
 *
 * Anthony: "add the same button that we did from leads to comms so they
 * can find where to respond." The customer's Comms tile had this link for
 * the main desk. The lead card now carries it too, so the one button reads
 * the same wherever somebody is looking at a thread and wants to answer it.
 */
export async function textBackLink(phone: string | null | undefined): Promise<string> {
  const p10 = ten(phone);
  return p10.length === 10 && await canTextFromPanel()
    ? `<div class="pad"><a class="btn ghost sm" href="#/comms/${p10}">Text back in the comms window &rarr;</a></div>`
    : "";
}

const ICON_FOR: Record<Comm["kind"], string> = {
  call: "leads", sms: "comms", email: "invoices", note: "manuals", system: "settings",
};

export function commRow(c: Comm & { customers?: { id: string; full_name: string } | null }): string {
  const out = c.direction === "outbound";
  const len = spoken(c.duration_seconds);
  /* A CallRail call's stored recording_url is the API address, which a
     browser cannot open. Its buttons fetch a playable one at press time.
     See ui/callplay.ts. Any other recording keeps its plain link. */
  const crCall = c.kind === "call" && c.raw?.callrail_id ? String(c.raw.callrail_id) : "";
  if (crCall) wireCallButtons();
  /**
   * THE TEXT DESK'S OWN ROWS. A member of staff asked the system something
   * and the system answered, so "We texted" would be wrong on both halves.
   * Anthony: "record this in the conversation section as from system to
   * tech, or system to supervisor". The who and the role ride on the row.
   */
  /**
   * DID IT ARRIVE. Outbound only — an inbound message is in front of you,
   * so it plainly arrived.
   *
   * Every tone is drawn, including the good one, and that is the point.
   * Before 2026-09-19 this row said nothing whatever about delivery, so a
   * message that never reached the handset looked exactly like one that
   * did: the AUTOFIX line's reply to Chance Pint showed here as answered
   * while his phone never rang. A pill only on failure would leave the
   * silent case ambiguous all over again — "no pill" would have to mean
   * both "delivered" and "nobody ever asked".
   *
   * Word AND color AND a leading dot, for the reason the .onoff switch
   * carries one: this is read at arm's length in a bay, and about one man
   * in twelve cannot separate the red from the green.
   */
  const dlv = out ? deliveryLabel(c.delivery_status) : null;
  const desk = c.raw?.text_desk ? c.raw : null;
  const person = desk ? `${desk.staff_name ?? "staff"}${desk.staff_role ? ` · ${desk.staff_role}` : ""}` : "";
  const head = desk
    ? (out ? `<b>System</b> <span class="comm-what">&rarr; ${esc(person)}${desk.forwarded ? " (forwarded)" : ""}</span>`
           : `<b>${esc(person)}</b> <span class="comm-what">&rarr; System</span>`)
    : `<b>${out ? "We" : c.customers?.full_name ?? "They"}</b>
          <span class="comm-what">${
            c.kind === "call" ? (out ? "called them" : "called in")
            : c.kind === "sms" ? (out ? "texted" : "texted in")
            : c.kind === "email" ? (out ? "emailed" : "emailed in")
            : esc(c.kind)}</span>`;
  return `
    <div class="comm ${out ? "out" : "in"}${desk ? " desk" : ""}">
      <span class="comm-ic">${icon(desk ? "settings" : ICON_FOR[c.kind] ?? "comms")}</span>
      <div class="comm-main">
        <div class="comm-head">
          ${head}
          ${len ? `<span class="pill">${esc(len)}</span>` : ""}
          ${c.tracking_number ? `<span class="pill" title="Which of the shop's numbers they rang">${
            esc(c.tracking_number)}</span>` : ""}
          ${dlv ? `<span class="dlv is-${dlv.tone}"
            title="What the carrier told us happened to this message">${esc(dlv.text)}</span>` : ""}
        </div>
        ${c.subject ? `<div class="comm-sub">${esc(c.subject)}</div>` : ""}
        ${c.body ? `<div class="comm-body">${esc(c.body)}</div>` : ""}
        ${dlv?.tone === "bad" && c.delivery_detail
          ? `<div class="comm-dlv-why">The carrier said: ${esc(c.delivery_detail)}</div>` : ""}
        ${crCall ? callButtonsHtml(crCall)
          : c.recording_url ? `<a class="comm-rec" href="${esc(c.recording_url)}"
          target="_blank" rel="noopener">Listen to the recording &#8599;</a>` : ""}
      </div>
      <div class="comm-when">${esc(ago(c.occurred_at))}</div>
    </div>`;
}

/** Rendered into a host on the customer page. */
export async function customerComms(hostId: string, customerId: string, phone: string | null): Promise<void> {
  const host = document.getElementById(hostId);
  if (!host) return;
  try {
    const rows = await commsForCustomer(customerId, phone, 60);

    /**
     * THE LAST THING SAID, AND A WAY TO SEE THE REST.
     *
     * Anthony: "it should show last message and a show more option to fold
     * out all." Sixty messages inline pushed everything under it off the
     * screen, and the one that matters is almost always the newest — the
     * rest is history you go looking for, not history you read past.
     *
     * The fold is a <details>, so it keeps its own state, works with no
     * JavaScript at all, and a browser's find-in-page can still reach
     * inside it.
     */
    const newest = rows[0];
    const older = rows.slice(1);

    // For a shop that can text from the panel, a one-tap jump from this
    // read-only tile straight to the two-pane texting window, opened on
    // this customer's own thread.
    const jump = await textBackLink(phone);

    host.innerHTML = `<div class="card-head">What was said &middot; ${rows.length}</div>` + (rows.length
      ? `<div class="commlist">${newest ? commRow(newest) : ""}</div>` + (older.length
          ? `<details class="commmore">
               <summary>Show the other ${older.length}
                 ${older.length === 1 ? "message" : "messages"}</summary>
               <div class="commlist">${older.map(commRow).join("")}</div>
             </details>`
          : "")
      : `<div class="empty">Nothing recorded with this customer yet.</div>`) + jump;
  } catch (e) {
    // A missing table is not worth breaking the whole customer page over.
    host.innerHTML = `<div class="card-head">What was said</div>
      <div class="empty">${esc(e instanceof Error ? e.message : String(e))}</div>`;
  }
}

const KINDS = [
  { key: "", label: "Everything" },
  { key: "call", label: "Calls" },
  { key: "sms", label: "Texts" },
  { key: "email", label: "Email" },
];
let kind = "";

/**
 * For a shop that can text from the panel this is a two-pane conversation
 * panel you can text from; for a shop with no line of its own it stays the
 * read-only ledger it has always been. whoAmI answers which, so the
 * composer is drawn only where sending from the panel is switched on.
 */
export async function commsPage(openTo?: string): Promise<void> {
  if (canTextFrom(await whoAmI())) return void deskPanel(openTo);
  return void flatLedger();
}

/* THE READ-ONLY LEDGER — a shop with no texting line of its own. */
async function flatLedger(): Promise<void> {
  mount(`<div class="page">
      <header class="page-head"><a class="back" href="#/more">&larr; Everything else</a>
        <h1>Comms</h1>
        <p>Every call, text and email on the shop. Reading only &mdash; sending stays where it is.</p>
      </header>
      <div class="tabs wide" id="comm-tabs">${KINDS.map((k) =>
        `<button class="tab ${k.key === kind ? "on" : ""}" data-k="${k.key}">${esc(k.label)}</button>`).join("")}</div>
      <div class="card" id="comm-list"><div class="loading">Reading&hellip;</div></div>
    </div>`);

  document.querySelectorAll<HTMLButtonElement>("#comm-tabs .tab").forEach((b) =>
    b.addEventListener("click", () => { kind = b.dataset["k"] ?? ""; void flatLedger(); }));

  const host = document.getElementById("comm-list");
  if (!host) return;
  try {
    const all = await recentComms(300);
    const rows = kind ? all.filter((c) => c.kind === kind) : all;
    host.innerHTML = rows.length
      ? `<div class="commlist">${rows.map(commRow).join("")}</div>`
      : `<div class="empty">Nothing here.</div>`;
  } catch (e) {
    host.innerHTML = `<div class="empty bad">${esc(e instanceof Error ? e.message : String(e))}</div>`;
  }
}

/* THE CONVERSATION PANEL — any shop with its own line. Threads are grouped from the
   same comm_events ledger everything else reads; the reply box is the one
   new power, and it can only answer a number already in the ledger. */
let openPhone = "";
let query = "";
let cache: Comm[] = [];
let stopLive: (() => void) | undefined;

async function deskPanel(openTo?: string): Promise<void> {
  // Arriving from a customer's Comms tab (the six-pack) with their number:
  // open straight onto that thread instead of the "pick a conversation" rest.
  const want = String(openTo ?? "").replace(/\D/g, "").slice(-10);
  if (want.length === 10) openPhone = want;
  mount(`<div class="page">
      <header class="page-head"><a class="back" href="#/more">&larr; Everything else</a>
        <h1>Comms</h1>
        <p>Every conversation on the shop. Pick a thread to read it and text back &mdash;
           it updates on its own as replies come in.</p>
      </header>
      <div class="commpanel">
        <div class="comm-left">
          <section class="card pane">
            <div class="pane-head"><label class="field"><span>Name or number</span><input id="comm-find" type="search"></label></div>
            <div class="threadlist" id="comm-threads"><div class="loading">Reading&hellip;</div></div>
          </section>
          <!-- The reply box sits under the list, not at the foot of the
               conversation. Anthony: "place the box you actually message
               the customer in, under the conversation selector on mobile
               as well as desktop." On a phone the conversation is below
               this, and a box at its foot meant scrolling past the whole
               thread to answer it. -->
          <section class="card pane comm-reply" id="comm-reply" hidden></section>
        </div>
        <section class="card pane convo" id="comm-convo"><div class="empty">Pick a conversation.</div></section>
      </div>
      <p class="comms-foot"><a href="/app#/comms" id="comms-popout" rel="noopener">Open comms in its own window &#8599;</a></p>
    </div>`);

  const find = document.getElementById("comm-find") as HTMLInputElement | null;
  if (find) {
    find.value = query;
    find.addEventListener("input", () => { query = find.value; renderThreads(); });
  }

  // A dispatcher keeps comms up on a second screen; this pops the panel into
  // its own window so it can sit there while the rest of the desk is worked.
  document.getElementById("comms-popout")?.addEventListener("click", (e) => {
    e.preventDefault();
    window.open("/app#/comms", "sd-comms", "width=1180,height=860");
  });

  try {
    cache = await recentComms(400);
  } catch (e) {
    const host = document.getElementById("comm-threads");
    if (host) host.innerHTML = `<div class="empty bad">${esc(e instanceof Error ? e.message : String(e))}</div>`;
    return;
  }
  renderThreads();
  renderConvo();
  renderReply();
  startWatching();
}

/**
 * WATCH FOR NEW MESSAGES WHILE THE PANEL IS OPEN. Anthony: "make it update as
 * we get responses and are on the screen. I had to refresh to see patricks
 * next message." A new comm_event now arrives by subscription (src/data/live.ts)
 * and runs tick() the instant it lands; a slow safety sweep stays behind it for
 * a dropped socket. tick() repaints the thread list only when the traffic
 * changes, and redraws the open conversation only when a new message lands in
 * it. The reply box is a separate card that a live refresh never redraws,
 * so a half-typed reply is never eaten. When the panel leaves
 * the screen the subscription and the sweep both stop themselves.
 */
function startWatching(): void {
  stopLive?.();
  stopLive = startLive({
    channel: "comms",
    table: "comm_events",
    event: "INSERT",
    refresh: tick,
    alive: () => !!document.getElementById("comm-threads"),
  });
}

async function tick(): Promise<void> {
  if (!document.getElementById("comm-threads")) {
    stopLive?.(); stopLive = undefined;
    return; // panel is gone — stop watching
  }
  let fresh: Comm[];
  try { fresh = await recentComms(400); } catch { return; }
  if (!document.getElementById("comm-threads")) return; // navigated away mid-fetch

  const openBefore = openPhone ? buildThreads().find((t) => t.phone === openPhone)?.last.id : undefined;
  const sameTop = (fresh[0]?.id ?? "") === (cache[0]?.id ?? "") && fresh.length === cache.length;
  cache = fresh;
  if (!sameTop) renderThreads();

  const openAfter = openPhone ? buildThreads().find((t) => t.phone === openPhone) : undefined;
  // The reply box lives in its own card now, so redrawing the conversation
  // cannot touch a half-typed reply or where the cursor sits.
  if (openAfter && openAfter.last.id !== openBefore) renderConvo();
}

/** Group the flat ledger into per-number threads, newest thread first. */
function buildThreads(): Thread[] {
  const by = new Map<string, Thread>();
  for (const c of cache) {
    const p = ten(c.phone);
    if (p.length !== 10) continue; // no number, no thread (notes, system, desk chatter)
    let t = by.get(p);
    if (!t) { t = { phone: p, name: "", rows: [], last: c, waiting: false }; by.set(p, t); }
    t.rows.push(c);
    const named = c as Comm & { customers?: { full_name?: string } | null };
    if (!t.name && named.customers?.full_name) t.name = named.customers.full_name;
  }
  const list: Thread[] = [];
  for (const t of by.values()) {
    t.last = t.rows[0]!;                 // recentComms is newest-first
    t.waiting = t.last.direction === "inbound";
    t.rows.reverse();                    // oldest-first for reading top to bottom
    list.push(t);
  }
  list.sort((a, b) => (a.last.occurred_at < b.last.occurred_at ? 1 : -1));
  return list;
}

function renderThreads(): void {
  const host = document.getElementById("comm-threads");
  if (!host) return;
  const q = query.trim().toLowerCase();
  const qd = q.replace(/\D/g, "");
  let list = buildThreads();
  if (q) list = list.filter((t) => t.name.toLowerCase().includes(q) || (qd !== "" && t.phone.includes(qd)));
  if (!list.length) { host.innerHTML = `<div class="empty">No conversations.</div>`; return; }
  host.innerHTML = list.map((t) => {
    const title = t.name || prettyPhone(t.phone);
    const prev = t.last.body ?? (t.last.kind === "call" ? "Phone call" : "");
    /* A thread whose last word never reached them is the one to open
       first, and it is invisible from a preview line that reads "You:
       ...". The whole list is scanned before any thread is opened, so the
       flag has to live out here as well as inside. */
    const lost = t.last.direction === "outbound" && neverArrived(t.last.delivery_status ?? null);
    return `<button class="thread${t.phone === openPhone ? " on" : ""}${t.waiting ? " waiting" : ""}${
        lost ? " undelivered" : ""}" data-p="${esc(t.phone)}">
        <div class="thread-top"><span class="thread-name">${esc(title)}</span>
          <span class="thread-when">${esc(ago(t.last.occurred_at))}</span></div>
        <div class="thread-prev">${lost ? `<b class="thread-lost">Not delivered &middot; </b>` : ""}${
          t.last.direction === "outbound" ? "You: " : ""}${esc(prev.slice(0, 90))}</div>
      </button>`;
  }).join("");
  host.querySelectorAll<HTMLButtonElement>(".thread").forEach((b) =>
    b.addEventListener("click", () => {
      openPhone = b.dataset["p"] ?? ""; renderThreads(); renderConvo(); renderReply();
    }));
}

function renderConvo(): void {
  const host = document.getElementById("comm-convo");
  if (!host) return;
  const t = openPhone ? buildThreads().find((x) => x.phone === openPhone) : undefined;
  if (!t) { host.innerHTML = `<div class="empty">Pick a conversation.</div>`; return; }
  const title = t.name || prettyPhone(t.phone);
  host.innerHTML = `
    <div class="convo-head"><b>${esc(title)}</b><span class="convo-num">${esc(prettyPhone(t.phone))}</span></div>
    <div class="convo-scroll" id="convo-scroll">${t.rows.map(commRow).join("")}</div>`;
  const scroll = document.getElementById("convo-scroll");
  if (scroll) scroll.scrollTop = scroll.scrollHeight;
}

/**
 * The reply box, in its own card under the thread list. It names who it
 * is going to, because on a phone the conversation it answers may be
 * scrolled out of sight below it. Drawn when the open thread CHANGES, never
 * on a live refresh, so a reply being typed is never redrawn out from under
 * somebody.
 */
function renderReply(): void {
  const host = document.getElementById("comm-reply");
  if (!host) return;
  const t = openPhone ? buildThreads().find((x) => x.phone === openPhone) : undefined;
  if (!t) { host.hidden = true; host.innerHTML = ""; return; }
  host.hidden = false;
  host.innerHTML = `
    <form class="reply" id="reply">
      <label class="field"><span>Text ${esc(t.name || prettyPhone(t.phone))}</span><textarea id="say" rows="3"></textarea></label>
      <div class="reply-row">
        <div class="msg-slot" id="reply-msg"></div>
        <button class="btn primary" id="reply-send" type="submit">Send</button>
      </div>
    </form>`;
  document.getElementById("reply")?.addEventListener("submit", (e) => { e.preventDefault(); void sendReply(); });
}

async function sendReply(): Promise<void> {
  const ta = document.getElementById("say") as HTMLTextAreaElement | null;
  const btn = document.getElementById("reply-send") as HTMLButtonElement | null;
  const note = document.getElementById("reply-msg");
  const msg = (ta?.value ?? "").trim();
  if (!msg || !btn) return;
  btn.disabled = true; btn.textContent = "Sending…";
  if (note) note.innerHTML = "";
  try {
    const r = await fetch(api("/api/sms/thread"), {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${await bearer()}` },
      body: JSON.stringify({ phone: openPhone, message: msg }),
    });
    const out = await r.json().catch(() => ({})) as { ok?: boolean; error?: string; warning?: string };
    if (!r.ok || !out.ok) throw new Error(out.error ?? "That did not send.");
    if (ta) ta.value = "";
    // The server wrote the outbound row before answering, so a re-read shows it.
    cache = await recentComms(400);
    renderThreads();
    renderConvo();
    renderReply();
    if (out.warning) {
      const n2 = document.getElementById("reply-msg");
      if (n2) n2.innerHTML = `<p class="msg good">${esc(out.warning)}</p>`;
    }
  } catch (e) {
    btn.disabled = false; btn.textContent = "Send";
    if (note) note.innerHTML = `<p class="msg bad">${esc(e instanceof Error ? e.message : String(e))}</p>`;
  }
}
